import type { QueryClient } from '@tanstack/react-query'

const API_BASE = import.meta.env['VITE_API_BASE_URL'] as string ?? 'http://localhost:8000'

// ── Token retrieval (for quick login, use dummy token) ──────────────────────────
let _getToken: (() => Promise<string | undefined>) | undefined

export function setTokenGetter(fn: () => Promise<string | undefined>) {
  _getToken = fn
}

export async function getAccessToken(): Promise<string> {
  return (await _getToken?.()) ?? ''
}

// ── Core fetcher (used by TanStack Query) ────────────────────────────────────
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await _getToken?.() || 'quick-login-token'

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.text().catch(() => response.statusText)
    throw new Error(`API ${response.status}: ${error}`)
  }

  if (response.status === 204) return undefined as unknown as T
  return response.json() as Promise<T>
}

// ── Convenience helpers ───────────────────────────────────────────────────────
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
}

// ── API Endpoints (Type-safe) ──────────────────────────────────────────────────

// Contacts
export const contactsApi = {
  list: () => api.get('/api/v1/contacts'),
  get: (id: string) => api.get(`/api/v1/contacts/${id}`),
  create: (data: any) => api.post('/api/v1/contacts', data),
  update: (id: string, data: any) => api.put(`/api/v1/contacts/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/contacts/${id}`),
  search: (q: string) => api.get(`/api/v1/contacts/search?q=${q}`),
}

// Accounts
export const accountsApi = {
  list: () => api.get('/api/v1/accounts'),
  get: (id: string) => api.get(`/api/v1/accounts/${id}`),
  create: (data: any) => api.post('/api/v1/accounts', data),
  update: (id: string, data: any) => api.put(`/api/v1/accounts/${id}`, data),
}

// Deals
export const dealsApi = {
  list: () => api.get('/api/v1/deals'),
  get: (id: string) => api.get(`/api/v1/deals/${id}`),
  create: (data: any) => api.post('/api/v1/deals', data),
  update: (id: string, data: any) => api.put(`/api/v1/deals/${id}`, data),
  byStage: (stage: string) => api.get(`/api/v1/deals?stage=${stage}`),
}

// Activities
export const activitiesApi = {
  list: (entityType: string, entityId: string) =>
    api.get(`/api/v1/activities?entity_type=${entityType}&entity_id=${entityId}`),
  create: (data: any) => api.post('/api/v1/activities', data),
}

// Products
export const productsApi = {
  list: () => api.get('/api/v1/products'),
  get: (id: string) => api.get(`/api/v1/products/${id}`),
  create: (data: any) => api.post('/api/v1/products', data),
  update: (id: string, data: any) => api.put(`/api/v1/products/${id}`, data),
}

// Consent
export const consentApi = {
  check: (contactId: string, channel: string) =>
    api.get(`/api/v1/consent/${contactId}/${channel}`),
  grant: (contactId: string, channel: string) =>
    api.post(`/api/v1/consent/${contactId}/${channel}`, { status: 'granted' }),
  withdraw: (contactId: string, channel: string) =>
    api.post(`/api/v1/consent/${contactId}/${channel}`, { status: 'withdrawn' }),
}

// ── AI SWARM API ──────────────────────────────────────────────────────────────
export const aiApi = {
  // Start a new AI run
  startRun: (payload: {
    goal: string
    trigger_event: string
    context: Record<string, any>
  }) => api.post('/api/v1/ai/runs', payload),

  // Get run status
  getRun: (runId: string) => api.get(`/api/v1/ai/runs/${runId}`),

  // Approve/reject actions (HITL)
  approveAction: (runId: string, approved: boolean, feedback?: string) =>
    api.post(`/api/v1/ai/runs/${runId}/approve`, { approved, feedback }),

  // Stream SSE updates
  streamRun: async (runId: string) => {
    const token = await _getToken?.() || 'quick-login-token'
    return new EventSource(`${API_BASE}/api/v1/ai/runs/${runId}/stream?token=${encodeURIComponent(token)}`) as EventSource
  },
}

// Import/Export
export const importExportApi = {
  import: async (formData: FormData) => {
    const token = await _getToken?.() || 'quick-login-token'
    const res = await fetch(`${API_BASE}/api/v1/imports`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    return res.json()
  },

  export: (entityType: string, filters?: Record<string, any>) =>
    api.post(`/api/v1/exports`, { entity_type: entityType, filters }),
}

// Calendar
export const calendarApi = {
  events: () => api.get('/api/v1/calendar/events'),
  createEvent: (data: any) => api.post('/api/v1/calendar/events', data),
}

// Tasks
export const tasksApi = {
  list: () => api.get('/api/v1/tasks'),
  create: (data: any) => api.post('/api/v1/tasks', data),
  update: (id: string, data: any) => api.put(`/api/v1/tasks/${id}`, data),
  complete: (id: string) => api.post(`/api/v1/tasks/${id}/complete`, {}),
}

// Admin
export const adminApi = {
  teamSettings: () => api.get('/api/v1/admin/team-settings'),
  updateTeamSettings: (data: any) => api.put('/api/v1/admin/team-settings', data),
  users: () => api.get('/api/v1/admin/users'),
  inviteUser: (email: string, role: string) =>
    api.post('/api/v1/admin/users/invite', { email, role }),
}

